import { ReactNode, useEffect, useState } from "react";
import { Collapse, Grid, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { differenceInYears, format, parseISO } from "date-fns";

import { useManageModal } from "../../../Manage/components/ManageModal";
import { useSlideInContext } from "../../../../context/SlideInContext";
import { useStudentContext } from "../../../../context/studentContext";
import usePersonAuthStore, { decodedToken } from "../../../store/personAuthStore";
import SendIcon from '@mui/icons-material/Send';
import { isNullOrEmpty } from "../../../../library";
import usePersonStore from "../../../store/personStore";
import { SCREEN_TYPE_REQUEST_OPTION, SelectLaunchPerson, SelectScreenProps, SelectLaunchPersonProps, SelectScreenVersion } from "../components";
import { dateOnlyISO8601 } from "../../../../library/date";
import SendToWhichEmail, { REQ_EMAIL_OPTIONS } from "../components/launchComponents/SendToWhichEmail";
import { PersonDataType } from "../../../../context/SelectedPersonContext";
import { SCREEN_SUBSCRIPTION_OPTION } from "../components/launchComponents/SelectScreenVersion";
type SEND_SESSION_REQUEST_ACTION = "local" | "redirect" | "notification" | "link";

function LaunchButton({ action: type, currentAction: action, onAction, children }: {
    action: SEND_SESSION_REQUEST_ACTION,
    currentAction: SEND_SESSION_REQUEST_ACTION | null,
    onAction: (action: SEND_SESSION_REQUEST_ACTION) => {}, children: ReactNode
}) {
    return (
        <LoadingButton
            sx={{
                backgroundColor: "primary.main",
                "&:hover": {
                    backgroundColor: "primary.light",
                },
                width: 180,
                marginTop: "16px",
                padding: 1
            }}
            type="submit"
            variant="contained"
            loading={type === action}
            loadingPosition="end"
            disabled={Boolean(action)}
            endIcon={<SendIcon />}
            onClick={() => onAction(type)}
        >
            {children}
        </LoadingButton>
    )
}

export default function LaunchForOthers() {
    const { openModal } = useManageModal();
    const { disableClosingDrawer, toggleDrawer } = useSlideInContext();
    const { studentData } = useStudentContext();
    const [launchAction, setLaunchAction] = useState<SEND_SESSION_REQUEST_ACTION | null>(null);
    const [screenType, setScreenType] = useState<SCREEN_SUBSCRIPTION_OPTION | null>(null);
    const [launchPerson, setLaunchPerson] = useState<string | null>(null);
    const [reqEmail, setReqEmail] = useState<REQ_EMAIL_OPTIONS | null>(null)
    const [remoteLoadStatus, setRemoteLoadStatus] = useState<boolean>(false)
    const locationId = usePersonStore(state => state.company.defaultLocation?.id);

    const accessToken = usePersonAuthStore((state) => state.access_token);

    const handleLaunch = async (action: SEND_SESSION_REQUEST_ACTION) => {

        // Prevent accidental closing of the application drawer
        disableClosingDrawer(true);

        if (action === "notification" && isNullOrEmpty(studentData.parentEmail) && isNullOrEmpty(studentData.email)) {
            handleNoEmailFound();
            return;
        }

        setLaunchAction(action);

        if (accessToken) {

            // Sleep for a simulated 10 seconds if intent is to send an email
            if (action === "notification") {
                openModal({ type: "REMOTE_LAUNCH_INITIALIZED", eventType: 'DISABLE_BACKDROP_AND_CTA', cancelButtonLabel: "Close" });
                // await sleep(1000 * 10)
            }
            const screenTypeParsed = screenType?.startsWith("TNS") ? "TeenScreen" : screenType?.startsWith("TS") ? "tScreen" : "TweenScreen";
            const languageParsed = screenType?.endsWith("ES") ? "Spanish" : "English";
            const { lid } = decodedToken(accessToken);
            const resposne = await fetch(`${window.__env.API_URL}/api/session/send`, {
                method: "post",
                headers: {
                    Pid: launchPerson!,
                    Lid: locationId ?? lid,
                    "Content-Type": "application/json",
                    authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    action: action,
                    type: screenTypeParsed,
                    language: languageParsed,
                    studentId: studentData.id,
                    email: reqEmail,
                    enteredAt: format(new Date(), "yyyy-MM-dd'T'HH:mm:ssXXX")
                }),
            });
            if (resposne.status >= 400) {
                openModal({ type: "REMOTE_LAUNCH_FAILURE", cancelButtonLabel: "close" })
            }

            if (resposne.status === 202) {
                handleSuccessMessage();
                return;
            }

            if (resposne.status === 200) {
                const json = await resposne.json();
                if (action == "link") {
                    openModal({ type: "LINK_PREPARED", cancelButtonLabel: "close", sessionLink: json.redirectUrl, modalAction: () => handleCopyLink(json.redirectUrl) })
                } else {
                    window.location.replace(json.redirectUrl);
                    window.sessionStorage.clear();
                }
            }
        }
        setRemoteLoadStatus(false);
        disableClosingDrawer(false);
        setLaunchAction(null);
    };
    const handleCopyLink = async (link: string): Promise<void> => {
        try {
            await navigator.clipboard.writeText(link)
        } catch (err) {
            console.log("err", err)
        }
    }
    const handleNoEmailFound = () => {
        toggleDrawer();
        disableClosingDrawer(false);
        openModal({ type: "NO_EMAIL_FOUND", cancelButtonLabel: "Close" });
    };
    const handleSelectPerson = async (person: string) => {
        setLaunchPerson(person)
    }
    const handleSelectScreen = async (screen: SCREEN_SUBSCRIPTION_OPTION) => {
        setScreenType(screen)
    }
    const handleEmailSelect = async (email: REQ_EMAIL_OPTIONS) => {
        await setReqEmail(email)
        setRemoteLoadStatus(true)
        // await handleLaunch("notification")
    }
    useEffect(() => {
        if (reqEmail) {
            handleLaunch("notification")
        }
    }, [reqEmail])
    const handleSuccessMessage = () => {
        toggleDrawer();
        disableClosingDrawer(false);
        openModal({ type: "REMOTE_LAUNCH_SUCCESS", eventType: 'DISABLE_BACKDROP', cancelButtonLabel: "Close" });
    };

    function getRecommendedScreen(studentAge: number) {
        // NOTE: FOR NOW WE COMMENT THIS OUT BECAUSE WE ONLY HAVE TWEENSCREEN HERE
        if (studentAge < 10) {
            return "tScreen"
        } else if (studentAge >= 10 && studentAge < 13) {
            return "TweenScreen"
        } else if (studentAge >= 13) {
            return "TeenScreen"
        } else {
            return "TweenScreen"
        }
    }
    function SelectScreenWrapper({ onSelect }: SelectScreenProps) {
        let dateISO = parseISO(dateOnlyISO8601(studentData.dob.toString()))
        const studentAge = differenceInYears(new Date(), dateISO)
        const recommendedScreen = getRecommendedScreen(studentAge)
        return <Grid container spacing={1} direction="column"
            alignItems="center"
            sx={{ textAlign: "center" }}>
            <Grid size={12} direction="column" alignItems="center">
                <Typography>Based on the child's age ({studentAge}) we recommend they take the <strong>{recommendedScreen}</strong></Typography>
            </Grid>
            <SelectScreenVersion onSelect={onSelect} />
        </Grid>
    }
    function SelectLaunchPersonWrapper({ onSelect }: SelectLaunchPersonProps) {
        return <Grid container spacing={2} direction="column"
            alignItems="center"
            sx={{ padding: "36px 16px", textAlign: "center" }}>
            <SelectLaunchPerson onSelect={onSelect} />
        </Grid>
    }
    // if (!launchPerson) {
    //     return <SelectLaunchPersonWrapper onSelect={handleSelectPerson} />
    // }
    // if (!screenType && launchPerson) {
    //     return <SelectScreenWrapper onSelect={handleSelectScreen} />
    // }
    return (
        <Grid
            container
            spacing={1}
            direction="column"
            alignItems="center"
            sx={{ padding: "36px 16px", textAlign: "center" }}
        >
            {!screenType ? (
                <>
                    <SelectLaunchPerson onSelect={handleSelectPerson} />
                    <Collapse in={launchPerson != null}>
                        <SelectScreenWrapper onSelect={handleSelectScreen} />
                    </Collapse></>
            ) : (
                <><Grid size={12} alignItems="center">
                    <Typography>
                        Launch the child's assessment in your browser window immediately.
                    </Typography>
                    <LaunchButton
                        action="local"
                        currentAction={launchAction}
                        onAction={handleLaunch}
                    >
                        Local Launch
                    </LaunchButton>
                </Grid>
                    <Grid size={12} alignItems="center">
                        <Typography>
                            Send a secure email so the child can take the
                            assessment remotely.
                        </Typography>
                        <Grid container>
                            <SendToWhichEmail onSelect={handleEmailSelect} loadStatus={remoteLoadStatus} />
                        </Grid>
                    </Grid>
                    <Grid size={12} direction="column" alignItems="center">
                        <Typography>
                            Prepare a link that I will send myself.
                        </Typography>
                        <LaunchButton
                            action="link"
                            currentAction={launchAction}
                            onAction={handleLaunch}
                        >
                            Generate Link
                        </LaunchButton>
                    </Grid></>
            )}

        </Grid>
    );
}
